import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Markerar en tråd som läst för inloggad medlem. */
export const markThreadRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ post_id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("forum_reads")
      .upsert(
        { user_id: userId, post_id: data.post_id, last_read_at: new Date().toISOString() },
        { onConflict: "user_id,post_id" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Returnerar tidpunkten då medlemmen senast läste tråden (innan detta besök). */
export const getThreadReadState = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ post_id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase
      .from("forum_reads")
      .select("last_read_at")
      .eq("user_id", userId)
      .eq("post_id", data.post_id)
      .maybeSingle();
    return { lastReadAt: row?.last_read_at ?? null };
  });

/** Vilka av dessa trådar har ny aktivitet sedan medlemmens senaste besök? */
export const getUnreadThreads = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ post_ids: z.array(z.string().uuid()).max(100) }).parse(input),
  )
  .handler(async ({ context, data }) => {
    if (!data.post_ids.length) return { unread: [] as string[] };
    const { supabase, userId } = context;
    const [{ data: reads }, { data: posts }] = await Promise.all([
      supabase
        .from("forum_reads")
        .select("post_id, last_read_at")
        .eq("user_id", userId)
        .in("post_id", data.post_ids),
      supabase
        .from("forum_posts")
        .select("id, last_activity_at")
        .in("id", data.post_ids),
    ]);
    const readMap = new Map((reads ?? []).map((r) => [r.post_id, r.last_read_at]));
    const unread = (posts ?? [])
      .filter((p) => {
        const seen = readMap.get(p.id);
        if (!seen) return true;
        return new Date(p.last_activity_at).getTime() > new Date(seen).getTime();
      })
      .map((p) => p.id);
    return { unread };
  });
